import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, forkJoin, map } from 'rxjs';
import { HelperService } from '../common/helper.service';
import { IChartSetting, IPayerAdminSetting, IPayerSetting, ISystemUserSetting, IUserChartSetting, IUserSetting } from '../../../core/modules/admin-modules/settings/models/settings.model';
import { ChartType, SettingsName, SubSettingsName, settingDescription, settingDisplayNames, settingInputLabel, settingInputTypeMap, subSettingInputLabel, subSettingInputTypeMap } from '../../constants/settings-type-enum';
import { DynamicFormControlType } from '@waseel-asp/tpa-waseel-lib-staging';
import { API } from '../../constants/api-url';
import { HttpRequestService } from '../http-request/http-request.service';
import { UserType } from '../../constants/user-type-enum';
import { AuthService } from '../auth/auth.service';
import { PERMISSION } from '../../constants/permission';
import { Store } from '@ngxs/store';
import { AuthState } from '../../store/auth/auth.state';
import { ICommonAPIResponse } from '../../models/common-models';

@Injectable({
  providedIn: 'root'
})
export class SettingService {
  private helperService = inject(HelperService);
  private authService = inject(AuthService);
  private store = inject(Store);
  private httpService = inject(HttpRequestService);

  isDynamicChartVisible = new BehaviorSubject<boolean>(false);
  isPayerSettingChange = new BehaviorSubject<boolean>(false);

  getPayerSettingByKey(key: string): IPayerSetting | IPayerAdminSetting | null {
    const settings = this.store.selectSnapshot(AuthState.payerSettings);
    return settings?.find(item => item.name === key);
  }

  getValueFromStorePayerSetting(key: string): string | null {
    const setting = this.getPayerSettingByKey(key);
    return setting ? setting.value : null;
  }

  getUserSettings(): ISystemUserSetting[] {
    return this.store.selectSnapshot(AuthState.userSettings);
  }

  getUserSettingByKey(key: string): ISystemUserSetting | null {
    const settings = this.getUserSettings();
    return settings?.find(item => item.settingName == key);
  }

  getValueFromStoreSetting(key: string): string | null {
    const setting = this.getUserSettingByKey(key);
    return setting ? (setting.userValue ?? setting.systemValue) : null;
  }

  getValueFromSetting(setting: ISystemUserSetting): string | null {
    return setting ? (setting.userValue ?? setting.systemValue) : null;
  }

  getAllDefaultSettings(): Observable<IUserSetting[]> {
    return this.httpService.get(API.AUTHENTICATION.DEFAULT_SETTING, null, null, this.helperService.getSkipErrorToastyContext());
  }

  getAllSettings(userId: string, showLoader: boolean = false): Observable<ISystemUserSetting[]> {
    const payloadSystemSettings = this.helperService.createSettingsPayload(this.getUserTypeColumn(), 'true');
    const payloadUserSettings = this.helperService.createSettingsPayload('userid', userId);

    return forkJoin([
      this.httpService.post(`${API.CONFIGURATION.SYSTEM_SETTINGS}slist`, payloadSystemSettings, null, null, showLoader ? null : this.helperService.getSkipLoaderContext()),
      this.httpService.post(`${API.CONFIGURATION.USER_SETTINGS}slist`, payloadUserSettings, null, null, showLoader ? null : this.helperService.getSkipLoaderContext())
    ]).pipe(
      map(([systemSettings, userSettings]: any) => {
        this.isDynamicChartVisible.next(
          systemSettings.results?.find(s => s.name === SettingsName.ChartType)?.value === ChartType.Dynamic
        );
        const isSuperAdmin = this.authService.isSuperAdmin();
        return systemSettings.results.map(systemSetting => {
          const userSetting = userSettings.results.find(us => us.settingId === systemSetting.systemSettingId && !us.subSettingId);
          const inputType = settingInputTypeMap[systemSetting.name] ?? DynamicFormControlType.Text;

          if (isSuperAdmin || inputType !== DynamicFormControlType.SlideToggle || systemSetting.value === 'true') {
            const mergedSetting: ISystemUserSetting = {
              settingId: systemSetting.systemSettingId,
              settingName: systemSetting.name,
              systemValue: systemSetting.value,
              userId: userId,
              userValue: userSetting ? userSetting.userValue : null,
              isForAdmin: systemSetting.isForAdmin,
              isForTPA: systemSetting.isForTPA,
              isForProvider: systemSetting.isForProvider,
              isForPayer: systemSetting.isForPayer,
              isForPatient: systemSetting.isForPatient,
              isDeleted: userSetting ? userSetting.isDeleted : false,
              inputType: inputType,
              inputLabel: settingInputLabel[systemSetting.name],
              settingDescription: settingDescription[systemSetting.name] ?? null,
              settingDisplayName: settingDisplayNames[systemSetting.name],
              subSettings:
                systemSetting.subSettings?.filter(subSetting => !subSetting.isDeleted && this.isSubSettingsAccess(subSetting.name))
                  .map(subSetting => {
                    const userSubSetting = userSettings.results.find(us => us.subSettingId === subSetting.systemSubSettingId);
                    const subInputType = subSettingInputTypeMap[subSetting.name] ?? DynamicFormControlType.Text;
                    if (isSuperAdmin || subInputType !== DynamicFormControlType.SlideToggle || subSetting.value === 'true') {
                      return {
                        settingId: subSetting.systemSubSettingId,
                        parentSettingId: systemSetting.systemSettingId,
                        parentSystemValue: systemSetting.value,
                        settingName: subSetting.name,
                        systemValue: subSetting.value,
                        userValue: userSubSetting ? userSubSetting.userSubValue : null,
                        isDeleted: subSetting.isDeleted,
                        inputType: subInputType,
                        inputLabel: subSettingInputLabel[subSetting.name],
                        settingDescription: settingDescription[subSetting.name] ?? null
                      };
                    }
                    return null;
                  }).filter(Boolean) ?? []
            };
            return mergedSetting;
          }
          return null;
        }).filter(Boolean);
      })
    );
  }

  getPayerSettings(): Observable<IPayerSetting[] | IPayerAdminSetting[]> {
    const isSuperAdmin = this.authService.isSuperAdmin();
    const payload = this.helperService.createSettingsPayload('payerMasterSettingId');

    if (isSuperAdmin) {
      return this.httpService.post(`${API.CONFIGURATION.PAYER_MASTER_SETTINGS}slist`, payload, null, null, this.helperService.getSkipLoaderContext()).pipe(
        map((response: ICommonAPIResponse<IPayerAdminSetting>) => {
          if (!response || !response.results) return [];

          return response.results.map((setting: IPayerAdminSetting) => ({
            payerMasterSettingId: setting.payerMasterSettingId,
            name: setting.name,
            value: setting.value,
            inputType: settingInputTypeMap[setting.name] ?? DynamicFormControlType.Text,
            inputLabel: settingInputLabel[setting.name],
            settingDescription: settingDescription[setting.name] ?? null,
            settingDisplayName: settingDisplayNames[setting.name],
          } as IPayerAdminSetting));
        })
      );
    }
    else {
      return forkJoin([
        this.httpService.post(`${API.CONFIGURATION.PAYER_MASTER_SETTINGS}slist`, payload, null, null, this.helperService.getSkipLoaderContext()),
        this.httpService.post(`${API.CONFIGURATION.PAYER_SETTINGS}slist`, payload, null, null, this.helperService.getSkipLoaderContext())
      ]).pipe(
        map(([masterResponse, payerResponse]: [ICommonAPIResponse<IPayerAdminSetting>, ICommonAPIResponse<IPayerSetting>]) => {
          const masterResults = masterResponse?.results || [];
          const payerResults = payerResponse?.results || [];
          const payerMap = new Map(payerResults.map(p => [p.payerMasterSettingId, p]));
          return masterResults.map(masterSetting => {
            const payerSetting = payerMap.get(masterSetting.payerMasterSettingId);
            const mergedSetting: IPayerSetting = {
              payerSettingId: payerSetting ? payerSetting.payerSettingId : null,
              payerMasterSettingId: masterSetting.payerMasterSettingId,
              name: masterSetting.name,
              value: (payerSetting && payerSetting.value) ? payerSetting.value : (masterSetting.value || ''),
              inputType: settingInputTypeMap[masterSetting.name] ?? DynamicFormControlType.Text,
              inputLabel: settingInputLabel[masterSetting.name],
              settingDescription: settingDescription[masterSetting.name] ?? null,
              settingDisplayName: settingDisplayNames[masterSetting.name],
            } as IPayerSetting;
            return mergedSetting;
          });
        })
      );
    }
  }

  getChartSettings(userId: string, isGetAllSettings?: boolean, showLoader: boolean = false): Observable<IChartSetting[] | IUserChartSetting[]> {
    const isSuperAdmin = this.authService.isSuperAdmin();
    if (isSuperAdmin && !isGetAllSettings) {
      const payload = this.helperService.createSettingsPayload('chartName');
      return this.httpService.post(`${API.CONFIGURATION.CHART_SETTINGS}slist`, payload, null, null, showLoader ? null : this.helperService.getSkipLoaderContext()).pipe(
        map((response: ICommonAPIResponse<IChartSetting>) => {
          if (!response || !response.results) return [];
          return response.results;
        })
      );
    } else {
      const payloadSystem = this.helperService.createSettingsPayload('chartName');
      const payloadUser = this.helperService.createSettingsPayload('userid', userId);
      return forkJoin([
        this.httpService.post(`${API.CONFIGURATION.CHART_SETTINGS}slist`, payloadSystem, null, null, showLoader ? null : this.helperService.getSkipLoaderContext()),
        this.httpService.post(`${API.CONFIGURATION.USER_CHART_SETTINGS}slist`, payloadUser, null, null, showLoader ? null : this.helperService.getSkipLoaderContext())
      ]).pipe(
        map(([systemResponse, userResponse]: [ICommonAPIResponse<IChartSetting>, ICommonAPIResponse<IUserChartSetting>]) => {
          const systemResults = systemResponse?.results || [];
          const userResults = userResponse?.results || [];
          const userMap = new Map(userResults.map(u => [u.chartSettingId, u]));
          return systemResults.filter(ss => ss.isEnabled).map(systemSetting => {
            const userSetting = userMap.get(systemSetting.chartSettingId);
            const mergedSetting = {
              userChartSettingId: userSetting ? userSetting.userChartSettingId : null,
              chartSettingId: systemSetting.chartSettingId,
              chartId: systemSetting.chartId,
              chartName: systemSetting.chartName,
              isEnabled: userSetting ? (userSetting.isChartSettingEnabledByUser === true) : systemSetting.isEnabled === true,
              isForTPA: systemSetting?.isForTPA === true,
              isForDashboard: systemSetting?.isForDashboard === true,
              isForReport: systemSetting?.isForReport === true,
              value: userSetting ? (userSetting.isChartSettingEnabledByUser === true) : systemSetting.isEnabled === true,
              inputType: DynamicFormControlType.SlideToggle,
              inputLabel: systemSetting.chartName,
              settingDescription: systemSetting.chartName,
              settingDisplayName: systemSetting.chartName,
            };
            return mergedSetting;
          });
        })
      );
    }
  }

  getUserTypeColumn(): string | null {
    if (this.authService.isSuperAdmin())
      return 'isForAdmin';
    else {
      switch (this.authService.currentUserType()) {
        case UserType.Provider: return 'isForProvider';
        case UserType.Patient: return 'isForPatient';
        case UserType.Payer: return 'isForPayer';
        case UserType.TPA: return 'isForTPA';
        default: return null;
      }
    }
  }

  isSubSettingsAccess(settingName: string): boolean {
    const permissionsPrefixMap: { [key: string]: string[] } = {
      'CLAIM': [SubSettingsName.ClaimActiveInActiveSummary, SubSettingsName.ClaimSummary],
      'DENIAL_MANAGEMENT': [SubSettingsName.DenialResponseSummary, SubSettingsName.DenialSummary],
      'ELIGIBILITY': [SubSettingsName.EligibilitySummary, SubSettingsName.EligibleNonEligibleSummary],
      'PAYMENT': [SubSettingsName.PaymentCompletionSummary, SubSettingsName.PaymentSummary],
      'CLAIM_CONVERT': [SubSettingsName.ConvertSummary],
      'PRE_AUTH': [SubSettingsName.PreAuthorizationSummary],
      'PRESCRIPTION': [SubSettingsName.PrescriptionSummary],
      'PAYMENT_RECONCILIATION': [SubSettingsName.ReconciliationSummary]
    };

    for (const [prefix, settings] of Object.entries(permissionsPrefixMap)) {
      if (settings.includes(settingName))
        return this.authService.hasAccess(`${PERMISSION[prefix].SCREEN}`) && this.authService.hasAccess(`${PERMISSION[prefix].LIST}`);
    }
    return true;
  }
}
